// src/config/resourceConfig.tsx
import React from 'react';
import {
    CorporateFare,
    People as UserIcon,
    Hotel as HotelIcon,
    ReceiptLong as TagIcon,
    Router as ControllerIcon,
    Tablet as HMIIcon,
    Apps as LockerIcon,
    ViewComfy as LockerTypeIcon,
    AssignmentTurnedIn as AssignmentIcon,
} from '@mui/icons-material';

// Import all components
import { GroupList, GroupCreate, GroupEdit } from '../features/groups';
import { UserList, UserCreate, UserEdit } from '../features/users';
import { HotelList, HotelCreate, HotelEdit } from '../features/hotels';
import { TagTypeList, TagTypeCreate, TagTypeEdit } from '../features/tag-types';
import { LockerControllerList, LockerControllerCreate, LockerControllerEdit } from '../features/locker-controllers';
import { HMIList, HMICreate, HMIEdit } from '../features/hmis';
import { LockerList, LockerCreate, LockerEdit } from '../features/lockers';
import { LockerTypeList, LockerTypeCreate, LockerTypeEdit } from  '../features/locker-types';
import { LockerAssignmentList, LockerAssignmentCreate, LockerAssignmentEdit } from '../features/locker-assignments';
import { getCurrentUser } from '../utils/auth';
import { HMISimulator } from '@/features/hmi-simulator';


// Función mejorada para verificar permisos
const hasResourcePermission = (resource: string, action: string): boolean => {
    try {
        // Obtener el rol actual del usuario
        const user = getCurrentUser();
        const currentRole = user?.role || localStorage.getItem('role');
        
        if (!currentRole) {
            console.warn('No role found for permission check');
            return false;
        }
        
        // Obtener la configuración del recurso
        const resourceConfig = resourcesConfig[resource];
        if (!resourceConfig) {
            console.warn(`Resource ${resource} not found in config`);
            return false;
        }
        
        // Verificar permisos
        const permissions = resourceConfig.permissions;
        const allowedRoles = permissions[action];
        
        if (!allowedRoles) {
            console.warn(`Action ${action} not found for resource ${resource}`);
            return false;
        }
        
        const hasPermission = allowedRoles.includes(currentRole);

        return hasPermission;
    } catch (error) {
        console.error('Error checking permissions:', error);
        return false;
    }
};

// Función para obtener elementos del menú
export const getMenuItems = () => {
    const menuItems = [];
    const user = getCurrentUser();
    const currentRole = user?.role || localStorage.getItem('role');
    
    if (!currentRole) {
        return [];
    }
    
    // Iterar sobre los recursos y agregar solo los que tienen permisos
    Object.values(resourcesConfig).forEach((resource: any) => {
        if (resource.showInMenu && hasResourcePermission(resource.name, 'access')) {
            menuItems.push({
                name: resource.name,
                textKey: resource.label,
                icon: resource.icon ? React.createElement(resource.icon) : null,
                route: resource.route,
                order: resource.menuOrder || 99,
            });
        }
    });
    
    // Ordenar por menuOrder
    return menuItems.sort((a, b) => a.order - b.order);
};

// Función principal para React Admin
export const getResourcesForReactAdmin = () => {
    const user = getCurrentUser();
    const currentRole = user?.role || localStorage.getItem('role');
    
    
    if (!currentRole) {
        console.warn('No role found, returning empty resources');
        return [];
    }
    
    // Convertir recursos a formato React Admin
    const resources = Object.values(resourcesConfig)
        .filter((resource: any) => {
            // Solo incluir recursos a los que el usuario tiene acceso
            const hasAccess = hasResourcePermission(resource.name, 'access');
            return hasAccess;
        })
        .map((resource: any) => ({
            name: resource.name,
            list: hasResourcePermission(resource.name, 'list') && resource.components.list
                ? resource.components.list 
                : undefined,
            create: hasResourcePermission(resource.name, 'create') && resource.components.create
                ? resource.components.create 
                : undefined,
            edit: hasResourcePermission(resource.name, 'edit') && resource.components.edit
                ? resource.components.edit 
                : undefined,
            show: hasResourcePermission(resource.name, 'show') && resource.components.show
                ? resource.components.show 
                : undefined,
            options: { label: resource.label },
            recordRepresentation: resource.recordRepresentation,
        }))
        .filter(resource => 
            // Solo incluir recursos que tienen al menos un componente válido
            resource.list || resource.create || resource.edit || resource.show
        );
    
    return resources;
};

// Configuración de recursos
export const resourcesConfig = {
    groups: {
        name: 'groups',
        recordRepresentation: 'name',
        route: '/groups',
        label: 'resources.groups.name',
        icon: CorporateFare,
        permissions: {
            access: ['SuperAdmin'],
            list: ['SuperAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin'],
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: GroupList,        
            create: GroupCreate,    
            edit: GroupEdit,        
            show: undefined,        
        },
        showInMenu: true,
        menuOrder: 1
    },

    users: {
        name: 'users',
        recordRepresentation: 'name', 
        route: '/users',
        label: 'resources.users.name',
        icon: UserIcon, 
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            show:[]
        },
        components: {
            list: UserList,
            create: UserCreate,
            edit: UserEdit,
            show: undefined, 
        },
        showInMenu: true,
        menuOrder: 2
    },

    hotels: {
        name: 'hotels',
        recordRepresentation: 'name',
        route: '/hotels',
        label: 'resources.hotels.name',
        icon: HotelIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin'],
            list: ['SuperAdmin', 'GroupAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin'],
            delete: ['SuperAdmin'],
            manageZones: ['SuperAdmin', 'GroupAdmin'],
            manageTexts: ['SuperAdmin', 'GroupAdmin'],
            manageNotifications: ['SuperAdmin', 'GroupAdmin'],
            manageFields: ['SuperAdmin', 'GroupAdmin'],
            show:[]
        },
        components: {
            list: HotelList,
            create: HotelCreate,
            edit: HotelEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 3,
    },

    'tag-types': {
        name: 'tag-types',
        recordRepresentation: 'name',
        route: '/tag-types',
        label: 'resources.tag-types.name',
        icon: TagIcon, 
        permissions: {
            access: ['SuperAdmin'],
            list: ['SuperAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin'],
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: TagTypeList,
            create: TagTypeCreate,
            edit: TagTypeEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 4
    },

    'locker-controllers': {
        name: 'locker-controllers',
        recordRepresentation: 'name', 
        route: '/locker-controllers',
        label: 'resources.locker-controllers.name',
        icon: ControllerIcon, 
        permissions: {
            access: ['SuperAdmin'], 
            list: ['SuperAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin'], 
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: LockerControllerList,
            create: LockerControllerCreate,
            edit: LockerControllerEdit,
            show: undefined, 
        },
        showInMenu: true,
        menuOrder: 5 
    },

    hmis: {
        name: 'hmis',
        recordRepresentation: 'name',
        route: '/hmis',
        label: 'resources.hmis.name',
        icon: HMIIcon, 
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            show:[]
        },
        components: {
            list: HMIList,
            create: HMICreate,
            edit: HMIEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 6
    },

    lockers: {
        name: 'lockers',
        recordRepresentation: 'number',
        route: '/lockers',
        label: 'resources.lockers.name',
        icon: LockerIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            show:[]
        },
        components: {
            list: LockerList,
            create: LockerCreate,
            edit: LockerEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 7
    },
    'locker-types': {
        name: 'locker-types',
        recordRepresentation: 'name',
        route: '/locker-types',
        label: 'resources.locker-types.name',
        icon: LockerTypeIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'],
            create: ['SuperAdmin', 'GroupAdmin'],
            edit: ['SuperAdmin', 'GroupAdmin'],
            delete: ['SuperAdmin', 'GroupAdmin'],
            show: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist']
        },
        components: {
            list: LockerTypeList,
            create: LockerTypeCreate,
            edit: LockerTypeEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 9
    },

    'locker-assignments': {
        name: 'locker-assignments',
        recordRepresentation: 'id',
        route: '/locker-assignments',
        label: 'resources.locker-assignments.name',
        icon: AssignmentIcon,
        permissions: {
            access: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            list: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            create: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            edit: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            delete: ['SuperAdmin', 'GroupAdmin', 'HotelAdmin', 'Receptionist'],
            show:[]
        },
        components: {
            list: LockerAssignmentList,
            create: LockerAssignmentCreate,
            edit: LockerAssignmentEdit,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 10
    },
    'hmi-simulator': {
        name: 'hmi-simulator',
        recordRepresentation: 'name',
        route: '/hmi-simulator',
        label: 'resources.hmi-simulator.name',
        icon: HMIIcon, 
        permissions: {
            access: ['SuperAdmin'],
            list: ['SuperAdmin'],
            create: ['SuperAdmin'],
            edit: ['SuperAdmin'],
            delete: ['SuperAdmin'],
            show:[]
        },
        components: {
            list: HMISimulator,
            create: undefined,
            edit: undefined,
            show: undefined,
        },
        showInMenu: true,
        menuOrder: 11
    },
};

// Mantener compatibilidad con función anterior
export const hasPermission = (resource: string, action: string, userRole?: string) => {
    return hasResourcePermission(resource, action);
};

export default resourcesConfig;